/**
 * Das Dial-Gate · Skript-Wahl vor jedem Anruf: zwei große Knöpfe, „Tim" oder „LK Skript".
 *
 * Vorgeschaltet vor `store.startCall(...)` für alle Wähl-Wege (Klassik, CallCockpit, Stapelkarte,
 * Telefon-Tabelle) — sie laufen alle über `useTrackedDial`, das `openDialGate` statt `startCall`
 * direkt ruft. Der Anruf startet erst mit der Wahl:
 *   - „Tim"      → das Cockpit zeigt das Skript von Tim (`callSkriptVon = 'tim'`)
 *   - „LK Skript" → das eigene, aktuell in der Skript-Bibliothek gültige Skript (wie bisher
 *                    „Ohne Wechsel anrufen")
 * Ist der Calltime-Modus an (`calltimeBis` in der Zukunft), entfällt die Wahl ganz: der Anruf
 * startet sofort mit Tims Skript. Davor stand hier der RegalPicker (Punkt 52, 2026-08-21); das
 * Übernehmen aus dem Regal bleibt in der Skript-Bibliothek selbst.
 *
 * Die Wahl erscheint nur Dienstag bis Donnerstag, 9 bis 12 Uhr (Berliner Zeit). Zu jeder anderen
 * Zeit startet der Anruf ohne Rückfrage mit dem eigenen Skript (LK). Der Calltime-Modus geht vor:
 * ist er an, gilt Tim auch außerhalb des Fensters.
 */
import { useEffect, useMemo } from 'react';
import { useStore } from '../../store';
import { CALLTIME_SKRIPT_VON } from '../../store/salesCrmSlice';

const WAHL_TAGE = new Set(['Tue', 'Wed', 'Thu']);
const WAHL_VON_STUNDE = 9;
const WAHL_BIS_STUNDE = 12;   // exklusiv: ab 12:00 keine Wahl mehr

/** Ist jetzt Di–Do zwischen 9 und 12 Uhr in Berlin? Zeitzone fest, nicht die des Browsers. */
export function wahlFensterOffen(jetzt: Date = new Date()): boolean {
  const teile = new Intl.DateTimeFormat('en-US', {
    timeZone: 'Europe/Berlin', weekday: 'short', hour: 'numeric', hourCycle: 'h23',
  }).formatToParts(jetzt);
  const tag = teile.find((p) => p.type === 'weekday')?.value ?? '';
  const stunde = Number(teile.find((p) => p.type === 'hour')?.value);
  return WAHL_TAGE.has(tag) && stunde >= WAHL_VON_STUNDE && stunde < WAHL_BIS_STUNDE;
}

export default function DialGate() {
  const pendingDial = useStore((s) => s.pendingDial);
  const resolveDialGate = useStore((s) => s.resolveDialGate);
  const calltimeBis = useStore((s) => s.calltimeBis);

  const calltimeAn = calltimeBis > Date.now();

  // Beim Öffnen des Gates festgehalten, nicht bei jedem Rendern neu gefragt: ein Fenster, das
  // mitten in der Wahl um Punkt 12 verschwindet, wäre schlimmer als eine Minute zu viel.
  const fensterOffen = useMemo(() => (pendingDial ? wahlFensterOffen() : false), [pendingDial]);

  useEffect(() => {
    if (!pendingDial) return;
    if (calltimeAn) resolveDialGate(undefined, CALLTIME_SKRIPT_VON);
    else if (!fensterOffen) resolveDialGate();
  }, [pendingDial, calltimeAn, fensterOffen, resolveDialGate]);

  if (!pendingDial || calltimeAn || !fensterOffen) return null;

  const knopf = 'flex-1 py-10 rounded-2xl text-2xl font-bold border transition-colors';

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
      <div className="bg-bg-surface border border-border-primary rounded-2xl w-full max-w-xl p-5">
        <p className="text-xs text-text-muted mb-3 text-center">Welches Skript für diesen Anruf?</p>
        <div className="flex gap-4">
          <button
            autoFocus
            onClick={() => resolveDialGate(undefined, CALLTIME_SKRIPT_VON)}
            className={`${knopf} bg-accent text-white border-accent hover:bg-accent/90`}
          >
            Tim
          </button>
          <button
            onClick={() => resolveDialGate()}
            className={`${knopf} bg-bg-elevated text-text-main border-border-primary hover:border-accent`}
          >
            LK Skript
          </button>
        </div>
      </div>
    </div>
  );
}
